import { Box, Button, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { FieldArray } from 'formik';
import { Add, ArrowDown2 } from 'iconsax-react';
import { type FC, type ReactElement, useContext, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectOrQuoteProductFormikType } from '@core/formik/projectOrQuoteProduct';
import type { ProductSectionType, TruckingTypeActions } from '@core/hooks/useProductSection';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import { hasValue } from '@core/utils/equality';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner';
import { SlabCard } from '../../SlabCard/SlabCard';
import { SlabCardBody } from '../../SlabCard/SlabCardBody';
import { SlabCardHeader } from '../../SlabCard/SlabCardHeader';
import { ProductTruckTypeSection } from './ProductTruckTypeSection';

type Props = {
  isProjectForm: boolean;
  productObject: Product | null;
  index: number;
  formikSectionType: ProductSectionType;
  readOnly: boolean;
  actions: TruckingTypeActions;
  actionsForAdditionalTruckingInfo: (
    index: number,
    additionalTruckingInfoIdx: number,
    plantProduct: Product | null,
  ) => TruckingTypeActions;
  addTruckingInfoRow: (index: number, plantProduct: Product | null) => void;
  formikProduct: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>;
};

export const ProductTruckTypesList: FC<Props> = ({
  isProjectForm,
  productObject,
  index,
  formikSectionType,
  readOnly,
  actions,
  actionsForAdditionalTruckingInfo,
  addTruckingInfoRow,
  formikProduct,
}) => {
  const ctx = useContext(SlabContext);
  const theme = useTheme();
  const [isTruckingInfoOpen, setIsTruckingInfoOpen] = useState<boolean>(true);

  const usesMultiTruckingTypes = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagMultiTruckEnabled,
  ]);
  const isApexProduct =
    productObject?.integrationSummary?.integrationType === Enums.IntegrationType.Apex;
  const isMultiTruckingAllowed = isApexProduct && usesMultiTruckingTypes;

  const {
    isLoading: isLoadingTruckingTypes,
    isError: isErrorTruckingTypes,
    data: truckingTypes,
  } = useSlabQuery('GET trucking type summaries', {
    queryParams: {
      filterBy: [
        {
          name: 'category',
          operation: Enums.FilterOperation.Equals,
          values: [productObject?.category ?? ''],
        },
        {
          name: 'plantId',
          operation: Enums.FilterOperation.Equals,
          values: [productObject?.plant?.id ?? ''],
        },
        {
          name: 'isArchived',
          operation: Enums.FilterOperation.Equals,
          values: ['false'],
        },
      ],
      perPage: 100,
      page: 0,
    },
  });

  if (isLoadingTruckingTypes) {
    return <LoadingSpinner />;
  }

  if (isErrorTruckingTypes || truckingTypes === undefined) {
    return <div>ERROR</div>;
  }

  if (!isMultiTruckingAllowed) {
    return (
      <ProductTruckTypeSection
        boxColor={theme.palette.SlabGray[50]}
        isProjectForm={isProjectForm}
        index={index}
        formikSectionType={formikSectionType}
        readOnly={readOnly}
        actions={actions}
        title='Trucking Info'
        truckingTypes={truckingTypes}
        currencyCode={productObject?.plant?.currencyCode}
      />
    );
  }

  const selectedAdditionalTruckingTypeCount = (formikProduct.additionalTruckingInfos ?? []).filter(
    (info) => hasValue(info.truckingType?.id),
  ).length;
  const hasSelectedMainTruckingType = hasValue(formikProduct.truckingType?.id);
  const isAddTruckingTypeDisabled =
    hasSelectedMainTruckingType &&
    selectedAdditionalTruckingTypeCount + 1 >= truckingTypes.items.length;

  return (
    <SlabCard
      sx={{
        backgroundColor: theme.palette.SlabGray[50],
      }}
    >
      <SlabCardHeader
        sx={{
          paddingY: 1,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
        }}
      >
        <IconButton
          sx={{
            transform: isTruckingInfoOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          onClick={() => {
            setIsTruckingInfoOpen((prev) => !prev);
          }}
        >
          <ArrowDown2 />
        </IconButton>
        <Typography variant='h5/medium'>Trucking Info:</Typography>
        <Box
          sx={{
            width: '34px',
            height: '26px',
            backgroundColor: theme.palette.SlabIndigo[500],
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color='white' fontSize='14px'>
            {formikProduct.additionalTruckingInfos.length + 1}
          </Typography>
        </Box>
      </SlabCardHeader>
      <Collapse in={isTruckingInfoOpen} timeout='auto' unmountOnExit>
        <SlabCardBody>
          <ProductTruckTypeSection
            isProjectForm={isProjectForm}
            index={index}
            formikSectionType={formikSectionType}
            readOnly={readOnly}
            actions={actions}
            title='Trucking Type 1'
            truckingTypes={truckingTypes}
            currencyCode={productObject?.plant?.currencyCode}
          />

          <FieldArray
            name={`${formikSectionType}.${index}.additionalTruckingInfos`}
            render={(additionalArrayHelpers): ReactElement => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {(formikProduct.additionalTruckingInfos ?? []).map(
                  (_info, additionalTruckingInfoIdx) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${formikSectionType}-${index}-additional-trucking-info-${additionalTruckingInfoIdx}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <ProductTruckTypeSection
                        onRemove={() => additionalArrayHelpers.remove(additionalTruckingInfoIdx)}
                        isProjectForm={isProjectForm}
                        index={index}
                        additionalTruckingInfoIndex={additionalTruckingInfoIdx}
                        formikSectionType={formikSectionType}
                        readOnly={readOnly}
                        actions={actionsForAdditionalTruckingInfo(
                          index,
                          additionalTruckingInfoIdx,
                          productObject,
                        )}
                        title={`Trucking Type ${additionalTruckingInfoIdx + 2}`}
                        truckingTypes={truckingTypes}
                        currencyCode={productObject?.plant?.currencyCode}
                      />
                    </Box>
                  ),
                )}
                {!readOnly && !isAddTruckingTypeDisabled && (
                  <Button
                    startIcon={<Add />}
                    disabled={isAddTruckingTypeDisabled}
                    sx={{
                      alignSelf: 'flex-start',
                      color: theme.palette.SlabIndigo['700'],
                      fontSize: '18px',
                    }}
                    onClick={() => addTruckingInfoRow(index, productObject)}
                  >
                    Trucking Type
                  </Button>
                )}
              </Box>
            )}
          />
        </SlabCardBody>
      </Collapse>
    </SlabCard>
  );
};
